import { useModal } from "@ebay/nice-modal-react";
import { useQuery } from "@tanstack/react-query";
import {
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  addMonths,
  differenceInDays,
  eachDayOfInterval,
  endOfMonth,
  format,
  formatISO,
  isAfter,
  isSameMonth,
  startOfDay,
  startOfMonth,
} from "date-fns";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts/highcharts";
import "highcharts/highcharts-more";
import "highcharts/modules/accessibility";
import "highcharts/modules/bullet";
import "highcharts/modules/solid-gauge";
import "highcharts/modules/stock";
import { useMemo, useState } from "react";
import { FaPencil } from "react-icons/fa6";
import { css } from "../../styled-system/css";
import { Box, HStack, VStack, Wrap } from "../../styled-system/jsx";
import { token } from "../../styled-system/tokens";
import Accordion from "../components/core/basic/Accordion";
import { Button } from "../components/core/basic/Button";
import Card from "../components/core/basic/Card";
import { PageContainer } from "../components/core/basic/PageContainer";
import BreadcrumbDates, { type Breadcrumb } from "../components/core/blocks/BreadcrumbDates";
import DetailedMetric from "../components/core/blocks/DetailedMetric";
import { DevErrorDisplay } from "../components/core/blocks/DevErrorDisplay";
import Loading from "../components/core/blocks/Loading";
import { CategoryCell } from "../components/custom/merchandise/cells/CategoryCell";
import { Datepicker } from "../components/fields/base/Datepicker";
import { createColumnBuilder } from "../components/table/columnBuilder";
import { RT8Pagination } from "../components/table/Pagination";
import { Table } from "../components/table/Table";
import { DatesProvider } from "../dates/DatesContext";
import { getCategoryReportOptions, type CategoryReportByCategoryRow } from "../modules/categories/api/categoryReport";
import { useCategoryGoalsQueryOptions } from "../modules/goals/api/useCategoryGoalsQueryOptions";
import { useOrgGoalsQueryOptions } from "../modules/goals/api/useOrgGoalsQueryOptions";
import { CategoryGoalsConfigModal, OrgGoalsConfigModal } from "../modules/goals/GoalsConfigurationModal";
import { useGetV4ProductReportByDateOpen } from "../modules/perf_reports/api/product/v4ProductReportByDate";
import { useGetV4ProductReportTotalsOpen } from "../modules/perf_reports/api/product/v4ProductReportTotals";
import { useAppSelector } from "../redux/hooks";
import { useModuleEnabled } from "../redux/selectors/modules";
import { lightenColor } from "../utils/colors";
import { AGENCY_DASHBOARD_BREADCRUMB_SPEC, DASHBOARD_LINKS } from "../utils/dashboardLinks";
import { formatCurrency, formatMetricValue, formatPercent, type FormatOptions } from "../utils/formatNumber";
import type { CategoryGoal, OrgGoal } from "../zodios/apiDefinitions/goals";
import { metrics } from "./merchandise/metrics";

declare module "highcharts" {
  interface Point {
    cumulativeSum: number;
  }
}

const LIGHTEN_AMOUNT = 1.3;

export function GoalsDashboard() {
  const hasAgencyDashboardModule = useModuleEnabled("AGENCY_DASHBOARD");
  const [displayPeriod, setDisplayPeriod] = useState(new Date());
  const startDate = startOfMonth(displayPeriod);
  const endDate = isSameMonth(displayPeriod, new Date()) ? startOfDay(new Date()) : endOfMonth(displayPeriod);
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const orgGoalQuery = useQuery(
    useOrgGoalsQueryOptions(
      formatISO(startDate, {
        representation: "date",
      }),
      formatISO(endDate, {
        representation: "date",
      }),
    ),
  );

  const configModal = useModal(OrgGoalsConfigModal);

  const totalsQuery = useQuery(useGetV4ProductReportTotalsOpen({ start: startDate, end: endDate }));
  if (totalsQuery.isLoading || orgGoalQuery.isLoading) {
    return <Loading />;
  }

  if (!totalsQuery.isSuccess) {
    return <DevErrorDisplay userMessage={"Error loading stats"} error={totalsQuery.error} />;
  }

  if (!orgGoalQuery.isSuccess) {
    return <DevErrorDisplay userMessage={"Error loading organization goals"} error={orgGoalQuery.error} />;
  }

  const goalBag = getGoalBag(orgGoalQuery.data, displayPeriod);

  const breadCrumbItems: Breadcrumb[] = [
    {
      name: "Goals Dashboard",
      href: "/user/goals",
      dropdownLinkOptions: DASHBOARD_LINKS,
      searchable: false,
      dropdownSubtitle: "Reports",
    },
  ];

  if (hasAgencyDashboardModule) {
    breadCrumbItems.unshift(AGENCY_DASHBOARD_BREADCRUMB_SPEC);
  }

  return (
    <DatesProvider>
      <BreadcrumbDates items={breadCrumbItems} hideDateSelector>
        <Button onPress={() => void configModal.show()}>Configure goals</Button>
      </BreadcrumbDates>
      <PageContainer withBreadcrumb>
        <VStack alignItems="stretch">
          <HStack alignSelf={"center"}>
            <Button
              variant={"contentOnly"}
              onPress={() => {
                setDisplayPeriod((prev) => addMonths(prev, -1));
              }}
              className={css({
                alignSelf: "end",
                paddingY: "xsmall",
                color: "text.secondary",
                fontSize: "32px",
                fontWeight: "bold",
                lineHeight: "1",
              })}
            >
              {"<"}
            </Button>
            <Datepicker
              value={displayPeriod.toISOString()}
              onChange={(val) => setDisplayPeriod(new Date(val!))}
              showMonthYearPicker
              dateFormat={"yyyy, MMMM"}
            />
            <Button
              variant={"contentOnly"}
              onPress={() => {
                setDisplayPeriod((prev) => addMonths(prev, 1));
              }}
              className={css({
                alignSelf: "end",
                paddingY: "xsmall",
                color: "text.secondary",
                fontSize: "32px",
                fontWeight: "bold",
                lineHeight: "1",
              })}
            >
              {">"}
            </Button>
          </HStack>
          {!goalBag ?
            <p>No goal set for this period</p>
          : <Card>
              <Accordion title="Sales Goals" initialExpanded>
                <Wrap gap="medium">
                  <Box flex={1} minWidth={"300px"}>
                    <GoalProgressBar
                      current={totalsQuery.data.data.total_sales ?? 0}
                      totalGoal={goalBag.totalGoal.totalSales}
                      todayGoal={goalBag.expectedToDate.totalSales}
                    />
                    <Wrap justifyContent={"space-evenly"} width="100%">
                      <DetailedMetric
                        name="Goal"
                        value={formatCurrency(goalBag.totalGoal.totalSales, marketPlace)}
                        center
                      />
                      <DetailedMetric
                        name="Actual"
                        value={formatCurrency(totalsQuery.data.data.total_sales ?? 0, marketPlace)}
                        target={formatCurrency(goalBag.expectedToDate.totalSales, marketPlace)}
                        targetLabel="vs. Expected"
                        center
                        showTarget
                        showTargetLabel
                      />
                    </Wrap>
                  </Box>
                  <Box flex={2}>
                    <TotalSalesChart
                      startDate={startDate}
                      endDate={endDate}
                      dailySalesGoal={goalBag.dailyGoal.totalSales}
                      daysInPeriod={goalBag.daysInPeriod}
                    />
                  </Box>
                </Wrap>
              </Accordion>
              <Accordion title="Ad Spend Budget" initialExpanded>
                <Wrap gap="medium">
                  <VStack flex={1} gap="medium">
                    <AdSpendChart
                      startDate={startDate}
                      endDate={endDate}
                      dailyAdSpendGoal={goalBag.dailyGoal.adSpend}
                      daysInPeriod={goalBag.daysInPeriod}
                    />
                    <PacingGauge
                      target={goalBag.expectedToDate.adSpend}
                      current={totalsQuery.data.data.cost ?? 0}
                      formatOptions={{
                        format: "currency",
                        compact: true,
                        decimalPlaces: 0,
                      }}
                      allowance={0.5}
                    />
                    <Wrap justifyContent={"space-evenly"} width="100%">
                      <DetailedMetric
                        name="Goal"
                        value={formatCurrency(goalBag.totalGoal.adSpend, marketPlace)}
                        center
                      />
                      <DetailedMetric
                        name="Actual"
                        value={formatCurrency(totalsQuery.data.data.cost ?? 0, marketPlace)}
                        target={formatCurrency(goalBag.expectedToDate.adSpend, marketPlace)}
                        targetLabel="vs. Expected"
                        center
                        showTarget
                        showTargetLabel
                      />
                    </Wrap>
                  </VStack>
                  <VStack flex={1} gap="medium">
                    <TacosChart
                      startDate={startDate}
                      endDate={endDate}
                      tacosGoal={goalBag.expectedToDate.adSpend / goalBag.expectedToDate.totalSales}
                      daysInPeriod={goalBag.daysInPeriod}
                    />
                    <PacingGauge
                      target={goalBag.expectedToDate.adSpend / goalBag.expectedToDate.totalSales}
                      current={totalsQuery.data.data.total_acos ?? 0}
                      formatOptions={{
                        format: "percent",
                        decimalPlaces: 0,
                      }}
                    />
                    <Wrap justifyContent={"space-evenly"} width="100%">
                      <DetailedMetric name="Goal" value={formatPercent(goalBag.totalGoal.tacos, 2)} center />
                      <DetailedMetric
                        name="Actual"
                        value={formatPercent(totalsQuery.data.data.total_acos ?? 0, 2)}
                        center
                      />
                    </Wrap>
                  </VStack>
                </Wrap>
              </Accordion>

              <Accordion title="Category Goals" initialExpanded>
                <CategoryTable startDate={startDate} endDate={endDate} displayPeriod={displayPeriod} />
              </Accordion>
            </Card>
          }
        </VStack>
      </PageContainer>
    </DatesProvider>
  );
}

function TotalSalesChart({
  startDate,
  endDate,
  dailySalesGoal,
  daysInPeriod,
}: {
  startDate: Date;
  endDate: Date;
  dailySalesGoal: number;
  daysInPeriod: number;
}) {
  const totalSalesGoalGraph = Array.from({ length: daysInPeriod }, () => dailySalesGoal);
  const totalSalesMetric = metrics.find((m) => m.id === "sales")!;
  const byDateQuery = useQuery(useGetV4ProductReportByDateOpen({ start: startDate, end: endDate }));
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);

  if (byDateQuery.isLoading) {
    return <Loading fullPage={false} height={300} />;
  }

  if (!byDateQuery.isSuccess) {
    return <DevErrorDisplay userMessage={"Error loading stats"} error={byDateQuery.error} />;
  }

  const options: Highcharts.Options = {
    title: { text: "" },
    chart: {
      type: "line",
      height: 350,
      style: {
        fontFamily: "Poppins",
        fontWeight: "300",
        fontSize: "15px",
      },
    },
    tooltip: {
      formatter() {
        return `<span style="color:${this.series.color as string}">${this.series.name}</span>: <b>${formatCurrency(this.cumulativeSum, marketPlace)}</b>`;
      },
    },
    xAxis: {
      categories: eachDayOfInterval({
        start: startDate,
        end: endDate,
      }).map((date) => format(date, "dd MMM")),
      labels: { step: 4 },
    },
    yAxis: {
      title: {
        text: null,
      },
    },
    series: [
      {
        name: `${totalSalesMetric.name} Expected`,
        data: totalSalesGoalGraph,
        type: "line",
        cumulative: true,
        color: lightenColor(totalSalesMetric.color, 1.9),
        dashStyle: "Dash",
        marker: { enabled: false },
      },
      {
        name: `${totalSalesMetric.name}`,
        data: byDateQuery.data.data.slice(0, daysInPeriod).map(totalSalesMetric.accessor),
        type: "line",
        cumulative: true,
        color: totalSalesMetric.color,
      },
    ],
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
}

function AdSpendChart({
  startDate,
  endDate,
  dailyAdSpendGoal,
  daysInPeriod,
}: {
  startDate: Date;
  endDate: Date;
  dailyAdSpendGoal: number;
  daysInPeriod: number;
}) {
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const adSpendGoalGraph = Array.from({ length: daysInPeriod }, (v, i) => [
    dailyAdSpendGoal * i * 0.9,
    dailyAdSpendGoal * i * 1.1,
  ]);

  const adSpendMetric = metrics.find((m) => m.id === "ad_spend")!;
  const byDateQuery = useQuery(useGetV4ProductReportByDateOpen({ start: startDate, end: endDate }));

  if (byDateQuery.isLoading) {
    return <Loading fullPage={false} height={300} />;
  }

  if (!byDateQuery.isSuccess) {
    return <DevErrorDisplay userMessage={"Error loading stats"} error={byDateQuery.error} />;
  }

  const options: Highcharts.Options = {
    title: { text: "" },
    chart: {
      type: "line",
      height: 300,
      style: {
        fontFamily: "Poppins",
        fontWeight: "300",
        fontSize: "15px",
      },
    },
    tooltip: {
      formatter() {
        return `<span style="color:${this.series.color as string}">${this.series.name}</span>: <b>${formatCurrency(this.cumulativeSum, marketPlace)}</b>`;
      },
    },
    xAxis: {
      categories: eachDayOfInterval({
        start: startDate,
        end: endDate,
      }).map((date) => format(date, "dd MMM")),
      labels: { step: 4 },
    },
    yAxis: {
      title: {
        text: null,
      },
    },
    series: [
      {
        name: `${adSpendMetric.name} Expected`,
        type: "arearange",
        data: adSpendGoalGraph,
        color: adSpendMetric.color,
        fillOpacity: 0.4,
        lineWidth: 0,
        marker: {
          enabled: false,
        },
        enableMouseTracking: false,
        zIndex: -1, // Ensure this series is below the line series
      },
      {
        name: `${adSpendMetric.name}`,
        data: byDateQuery.data.data.slice(0, daysInPeriod).map(adSpendMetric.accessor),
        type: "line",
        cumulative: true,
        color: adSpendMetric.color,
      },
    ],
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
}

function TacosChart({
  startDate,
  endDate,
  tacosGoal,
  daysInPeriod,
}: {
  startDate: Date;
  endDate: Date;
  tacosGoal: number;
  daysInPeriod: number;
}) {
  const tacosGoalGraph = Array.from({ length: daysInPeriod }, () => [tacosGoal * 0.9, tacosGoal * 1.1]);

  const tacosMetric = metrics.find((m) => m.id === "total_acos")!;
  const byDateQuery = useQuery(useGetV4ProductReportByDateOpen({ start: startDate, end: endDate }));

  if (byDateQuery.isLoading) {
    return <Loading fullPage={false} height={300} />;
  }

  if (!byDateQuery.isSuccess) {
    return <DevErrorDisplay userMessage={"Error loading stats"} error={byDateQuery.error} />;
  }

  const options: Highcharts.Options = {
    title: { text: "" },
    chart: {
      type: "line",
      height: 300,
      style: {
        fontFamily: "Poppins",
        fontWeight: "300",
        fontSize: "15px",
      },
    },
    tooltip: {
      formatter() {
        return `<span style="color:${this.series.color as string}">${this.series.name}</span>: <b>${formatPercent(this.y ?? 0, 2)}</b>`;
      },
    },
    xAxis: {
      categories: eachDayOfInterval({
        start: startDate,
        end: endDate,
      }).map((date) => format(date, "dd MMM")),
      labels: { step: 4 },
    },
    yAxis: {
      title: {
        text: null,
      },
      labels: {
        formatter: (v) => formatPercent(v.value as number, 0),
      },
    },
    series: [
      {
        name: `${tacosMetric.name} Expected`,
        type: "arearange",
        data: tacosGoalGraph,
        color: tacosMetric.color,
        fillOpacity: 0.4,
        lineWidth: 0,
        marker: {
          enabled: false,
        },
        enableMouseTracking: false,
        zIndex: -1, // Ensure this series is below the line series
      },
      {
        name: `${tacosMetric.name}`,
        data: byDateQuery.data.data.slice(0, daysInPeriod).map(tacosMetric.accessor),
        type: "line",
        color: tacosMetric.color,
      },
    ],
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
}

function PacingGauge({
  target,
  current,
  allowance = 0.15,
  formatOptions,
}: {
  target: number;
  current: number;
  allowance?: number;
  formatOptions: FormatOptions;
}) {
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const gaugeMin = target * (1 - allowance);
  const gaugeMax = target * (1 + allowance);
  const gaugeCurrent = Math.max(gaugeMin, Math.min(current, gaugeMax));

  const options: Highcharts.Options = {
    chart: {
      inverted: true,
      height: 50,
      style: {
        fontFamily: "Poppins",
        fontWeight: "300",
        fontSize: "15px",
      },
    },
    title: {
      text: "",
    },
    legend: {
      enabled: false,
    },
    plotOptions: {
      bullet: {
        pointPadding: 0.25,
        borderWidth: 0,
        targetOptions: {
          color: token("colors.trellisPalette.4"),
          width: 17,
        },
      },
    },
    xAxis: {
      visible: false,
      height: 10,
      min: 0,
      max: 0,
      maxPadding: 0,
    },
    yAxis: [
      {
        offset: 0,
        gridLineWidth: 0,
        tickPositions: [gaugeMin, target, gaugeMax],
        min: gaugeMin,
        max: gaugeMax,
        plotBands: [
          {
            from: gaugeMin,
            to: gaugeMax,
            color: {
              linearGradient: { x1: 0, x2: 1, y1: 0, y2: 0 },
              stops: [
                [0, lightenColor(token("colors.trellisPalette.17"), LIGHTEN_AMOUNT)],
                [0.2, lightenColor(token("colors.trellisPalette.16"), LIGHTEN_AMOUNT)],
                [0.35, lightenColor(token("colors.trellisPalette.16"), LIGHTEN_AMOUNT)],
                [0.5, lightenColor(token("colors.trellisPalette.12"), LIGHTEN_AMOUNT)],
                [0.65, lightenColor(token("colors.trellisPalette.16"), LIGHTEN_AMOUNT)],
                [0.8, lightenColor(token("colors.trellisPalette.16"), LIGHTEN_AMOUNT)],
                [1, lightenColor(token("colors.trellisPalette.17"), LIGHTEN_AMOUNT)],
              ],
            },
          },
        ],
        labels: {
          formatter: function () {
            return formatMetricValue(this.value as number, formatOptions, marketPlace);
          },
        },
        title: {
          text: null,
        },
      },
    ],
    series: [
      {
        type: "bullet",
        clip: false, // Allow target marker to overflow plot area
        data: [
          {
            target: gaugeCurrent,
          },
        ],
        dataLabels: {
          enabled: true,
        },
      },
    ],
  };

  return (
    <VStack>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </VStack>
  );
}

function getCurrentPeriodGoal(config: OrgGoal[] | undefined, displayPeriod: Date) {
  const goalKey = formatISO(new Date(displayPeriod.getFullYear(), displayPeriod.getMonth(), 1), {
    representation: "date",
  });
  const goalValue = config?.find((goal) => goal.start_date === goalKey);
  return goalValue ?? null;
}

function GoalProgressBar({ current, totalGoal, todayGoal }: { current: number; totalGoal: number; todayGoal: number }) {
  const pacing = current / todayGoal;
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);

  const extraTicks = todayGoal <= current || todayGoal >= totalGoal ? [current] : [current, todayGoal];

  const options: Highcharts.Options = {
    chart: {
      type: "solidgauge",
      height: 300,
    },

    title: { text: "" },
    tooltip: { enabled: false },
    legend: {
      enabled: false,
    },

    pane: {
      center: ["50%", "50%"],
      startAngle: -140,
      endAngle: 140,
      background: [
        {
          backgroundColor: token("colors.white"),
          borderColor: token.var("colors.border.default"),
          borderRadius: "50%",
          innerRadius: "75%",
          outerRadius: "100%",
          shape: "arc",
        },
      ],
    },
    // the value axis
    yAxis: [
      {
        min: 0,
        max: totalGoal,
        lineWidth: 0,
        tickWidth: 0,
        minorTickInterval: 0,
        tickPositions: [totalGoal],

        labels: {
          y: 26,
          formatter: (val) => formatCurrency(val.value as number, marketPlace, false, false, true),
          style: { fontSize: token("fontSizes.small") },
        },
      },
      {
        linkedTo: 0,
        lineWidth: 0,
        tickWidth: 0,
        minorTickInterval: 0,
        tickPositions: extraTicks,
        labels: {
          distance: 20,
          formatter: (val) => formatCurrency(val.value as number, marketPlace, false, false, true),
        },
      },
    ],

    plotOptions: {
      solidgauge: {
        dataLabels: {
          y: 5,
          borderWidth: 0,
        },
      },
    },
    series: [
      {
        type: "solidgauge",
        //@ts-expect-error Missing from Highcharts definitions https://github.com/highcharts/highcharts/issues/23396
        borderRadius: "50%",
        data: [
          {
            y: todayGoal,
            color: {
              pattern: {
                path: {
                  d: "M 0 0 L 10 10 M 9 -1 L 11 1 M -1 9 L 1 11",
                  strokeWidth: 3,
                  stroke: token("colors.trellisPalette.17"),
                  opacity: 1,
                },
                width: 10,
                height: 10,
                opacity: 0.3,
                backgroundColor: token("colors.trellisPalette.17"),
              },
            },
          },
          {
            y: current,
            color: token("colors.trellisPalette.12"),
          },
        ],
        innerRadius: "75%",
        radius: "100%",
        dataLabels: {
          enabled: true,
          formatter: () => `Pacing:<br/>${formatPercent(pacing, 2)}`,
          y: -20,
          style: {
            fontSize: token("fontSizes.small"),
          },
        },
      },
    ],
  };
  return <HighchartsReact highcharts={Highcharts} options={options} />;
}

function PacingGoalDisplay({ pacing }: { pacing: number }) {
  return (
    <span
      className={css({
        borderColor:
          pacing < 0.5 ? "trellisPalette.17"
          : pacing < 0.75 ? "trellisPalette.16"
          : "trellisPalette.12",
        borderRadius: "pill",
        borderWidth: "2px",
        paddingX: "xsmall",
        backgroundColor:
          pacing < 0.5 ? "trellisPalette.17/10"
          : pacing < 0.75 ? "trellisPalette.16/10"
          : "trellisPalette.12/10",
        borderStyle: "solid",
        whiteSpace: "nowrap",
      })}
    >
      {pacing > 1 ? "🔥 " : null}
      {formatPercent(pacing, 2)}
    </span>
  );
}

function PacingRangeDisplay({ pacing }: { pacing: number }) {
  return (
    <span
      className={css({
        borderColor:
          pacing < 0.5 ? "trellisPalette.17"
          : pacing < 0.85 ? "trellisPalette.16"
          : pacing > 1.5 ? "trellisPalette.17"
          : pacing > 1.15 ? "trellisPalette.16"
          : "trellisPalette.12",
        borderRadius: "pill",
        borderWidth: "2px",
        paddingX: "xsmall",
        backgroundColor:
          pacing < 0.5 ? "trellisPalette.17/10"
          : pacing < 0.85 ? "trellisPalette.16/10"
          : pacing > 1.5 ? "trellisPalette.17/10"
          : pacing > 1.15 ? "trellisPalette.16/10"
          : "trellisPalette.12/10",
        borderStyle: "solid",
        whiteSpace: "nowrap",
      })}
    >
      {formatPercent(pacing, 2)}
    </span>
  );
}

const columnHelper = createColumnBuilder<CategoryReportByCategoryRow>();

function CategoryTable({ startDate, endDate, displayPeriod }: { startDate: Date; endDate: Date; displayPeriod: Date }) {
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const channel = useAppSelector((state) => state.channel.currentChannel);

  const categoryReportQuery = useQuery(
    getCategoryReportOptions({
      mp: marketPlace,
      date_group: "day",
      start_date: formatISO(startDate, { representation: "date" }),
      end_date: formatISO(endDate, { representation: "date" }),
      pre_start_date: formatISO(startDate, { representation: "date" }),
      pre_end_date: formatISO(endDate, { representation: "date" }),
      channel,
      empty: true,
      group_by: "category_id",
    }),
  );

  const categoryGoalsQuery = useQuery({
    ...useCategoryGoalsQueryOptions(
      formatISO(startDate, { representation: "date" }),
      formatISO(endDate, { representation: "date" }),
    ),
    select(data) {
      return data.reduce(
        (acc, goal) => {
          if (!acc[goal.category_id]) {
            acc[goal.category_id] = [];
          }
          acc[goal.category_id]!.push(goal);
          return acc;
        },
        {} as Record<number, CategoryGoal[]>,
      );
    },
  });

  const columns = useMemo(
    () =>
      (() => [
        columnHelper.accessor("category_name", {
          header: "Category",
          cell: ({ row }) => <CategoryCell original={row.original} />,
          minSize: 200,
        }),
        columnHelper.accessor("total_sales", {
          header: "Total Sales",
          cell: ({ getValue, row }) => {
            const value = getValue();
            const toDateGoal =
              getGoalBag(categoryGoalsQuery.data?.[row.original.category_id], displayPeriod)?.expectedToDate
                .totalSales ?? 0;
            if (typeof value !== "number") {
              return "-";
            }
            return (
              <VStack
                gap={"small"}
                alignItems={"end"}
                width={"100%"}
                paddingY="xsmall"
                paddingX={"small"}
                lineHeight={"1"}
              >
                <span>{formatCurrency(getValue() ?? 0, marketPlace)}</span>
                <span>
                  {toDateGoal ?
                    <>
                      Pacing: <PacingGoalDisplay pacing={value / toDateGoal} />
                    </>
                  : null}
                </span>
              </VStack>
            );
          },
          minSize: 200,
        }),
        columnHelper.accessor("cost", {
          header: "Ad Spend",
          cell: ({ getValue, row }) => {
            const value = getValue();
            const toDateGoal =
              getGoalBag(categoryGoalsQuery.data?.[row.original.category_id], displayPeriod)?.expectedToDate.adSpend ??
              0;
            if (typeof value !== "number") {
              return "-";
            }
            return (
              <VStack
                gap={"small"}
                alignItems={"end"}
                width={"100%"}
                paddingY="xsmall"
                paddingX={"small"}
                lineHeight={"1"}
              >
                <span>{formatCurrency(value, marketPlace)}</span>
                {toDateGoal ?
                  <span>
                    Pacing: <PacingRangeDisplay pacing={value / toDateGoal} />
                  </span>
                : null}
              </VStack>
            );
          },
          minSize: 200,
        }),
        columnHelper.accessor("total_acos", {
          header: "TACOS",
          cell: ({ getValue }) => formatPercent(getValue() ?? 0, 2),
        }),
        columnHelper.accessor(
          (row) => getGoalBag(categoryGoalsQuery.data?.[row.category_id], displayPeriod)?.totalGoal.totalSales,
          {
            id: "total_sales_goal",
            header: "Total Sales Goal",
            cell: function Cell({ row }) {
              const goalBag = getGoalBag(categoryGoalsQuery.data?.[row.original.category_id], displayPeriod);
              const value = goalBag?.["totalGoal"].totalSales;
              return value ? formatCurrency(value, marketPlace) : "N/A";
            },
            sortUndefined: "last",
          },
        ),
        columnHelper.accessor(
          (row) => getGoalBag(categoryGoalsQuery.data?.[row.category_id], displayPeriod)?.totalGoal.adSpend,
          {
            id: "ad_spend_goal",
            header: "Ad Spend Goal",
            cell: function Cell({ row }) {
              const goalBag = getGoalBag(categoryGoalsQuery.data?.[row.original.category_id], displayPeriod);
              const value = goalBag?.["totalGoal"].adSpend;
              return value ? formatCurrency(value, marketPlace) : "N/A";
            },
            sortUndefined: "last",
          },
        ),
        columnHelper.accessor(
          (row) => {
            const goalBag = getGoalBag(categoryGoalsQuery.data?.[row.category_id], displayPeriod);
            if (!goalBag) {
              return null;
            }
            return goalBag.totalGoal.adSpend / goalBag.totalGoal.totalSales;
          },
          {
            id: "tacos_goal",
            header: "TACOS Goal",
            cell: ({ getValue }) => {
              const value = getValue();
              return value ? formatPercent(value, 2) : "N/A";
            },
            sortUndefined: "last",
          },
        ),

        columnHelper.display({
          id: "actions",
          header: "",
          size: 50,
          enableResizing: false,

          cell: function Cell({ row }) {
            const modal = useModal(CategoryGoalsConfigModal);
            return (
              <Button
                variant="contentOnly"
                onPress={() => {
                  void modal.show({ categoryId: row.original.category_id });
                }}
              >
                <FaPencil />
              </Button>
            );
          },
          meta: {
            justify: "center",
          },
        }),
      ])(),
    [marketPlace, displayPeriod, categoryGoalsQuery.data],
  );

  const table = useReactTable({
    data: categoryReportQuery.data?.categories ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowCanExpand: () => true,
    enableRowSelection: false,
  });

  return (
    <>
      <Wrap>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </Wrap>
      <Table
        table={table}
        renderExpander={function Expander(row) {
          const goalBag = getGoalBag(categoryGoalsQuery.data?.[row.original.category_id], displayPeriod);
          if (!goalBag) {
            return <p>No goal set for this category</p>;
          }
          return (
            <VStack gap="medium" alignItems="stretch" padding="medium">
              <Card>
                <Accordion title="Sales Goals" initialExpanded>
                  <Wrap gap="medium">
                    <Box flex={1} minWidth={"300px"}>
                      <GoalProgressBar
                        current={row.original.total_sales ?? 0}
                        totalGoal={goalBag.totalGoal.totalSales}
                        todayGoal={goalBag.expectedToDate.totalSales}
                      />
                      <Wrap justifyContent={"space-evenly"} width="100%">
                        <DetailedMetric
                          name="Goal"
                          value={formatCurrency(goalBag.totalGoal.totalSales, marketPlace)}
                          center
                        />
                        <DetailedMetric
                          name="Actual"
                          value={formatCurrency(row.original.total_sales ?? 0, marketPlace)}
                          target={formatCurrency(goalBag.expectedToDate.totalSales, marketPlace)}
                          targetLabel="vs. Expected"
                          center
                          showTarget
                          showTargetLabel
                        />
                      </Wrap>
                    </Box>
                    <Box flex={2}>
                      <TotalSalesChart
                        startDate={startDate}
                        endDate={endDate}
                        dailySalesGoal={goalBag.dailyGoal.totalSales}
                        daysInPeriod={goalBag.daysInPeriod}
                      />
                    </Box>
                  </Wrap>
                </Accordion>
                <Accordion title="Ad Spend Budget" initialExpanded>
                  <Wrap gap="medium">
                    <VStack flex={1} gap="medium">
                      <AdSpendChart
                        startDate={startDate}
                        endDate={endDate}
                        dailyAdSpendGoal={goalBag.dailyGoal.adSpend}
                        daysInPeriod={goalBag.daysInPeriod}
                      />
                      <PacingGauge
                        target={goalBag.expectedToDate.adSpend}
                        current={row.original.cost ?? 0}
                        formatOptions={{
                          format: "currency",
                          compact: true,
                          decimalPlaces: 0,
                        }}
                        allowance={0.5}
                      />
                      <Wrap justifyContent={"space-evenly"} width="100%">
                        <DetailedMetric
                          name="Goal"
                          value={formatCurrency(goalBag.totalGoal.adSpend, marketPlace)}
                          center
                        />
                        <DetailedMetric
                          name="Actual"
                          value={formatCurrency(row.original.cost ?? 0, marketPlace)}
                          target={formatCurrency(goalBag.expectedToDate.adSpend, marketPlace)}
                          targetLabel="vs. Expected"
                          center
                          showTarget
                          showTargetLabel
                        />
                      </Wrap>
                    </VStack>
                    <VStack flex={1} gap="medium">
                      <TacosChart
                        startDate={startDate}
                        endDate={endDate}
                        tacosGoal={goalBag.expectedToDate.adSpend / goalBag.expectedToDate.totalSales}
                        daysInPeriod={goalBag.daysInPeriod}
                      />
                      <PacingGauge
                        target={goalBag.expectedToDate.adSpend / goalBag.expectedToDate.totalSales}
                        current={row.original.total_acos ?? 0}
                        formatOptions={{
                          format: "percent",
                          decimalPlaces: 0,
                        }}
                      />
                      <Wrap justifyContent={"space-evenly"} width="100%">
                        <DetailedMetric name="Goal" value={formatPercent(goalBag.totalGoal.tacos, 2)} center />
                        <DetailedMetric name="Actual" value={formatPercent(row.original.total_acos ?? 0, 2)} center />
                      </Wrap>
                    </VStack>
                  </Wrap>
                </Accordion>
              </Card>
            </VStack>
          );
        }}
      />
    </>
  );
}

function getGoalBag(goalConfig: OrgGoal[] | undefined, displayPeriod: Date) {
  const isCurrentPeriod = isSameMonth(displayPeriod, new Date());
  const isFuturePeriod = !isCurrentPeriod && isAfter(displayPeriod, new Date());
  const currentGoal = getCurrentPeriodGoal(goalConfig, displayPeriod);

  if (!currentGoal) {
    return null;
  }

  const startDate = startOfMonth(displayPeriod);
  const endDate = endOfMonth(displayPeriod);

  const daysInPeriod = differenceInDays(endDate, startDate) + 1;
  const dailySalesGoal = currentGoal.total_sales_goal / daysInPeriod;
  const dailyAdSpendGoal = currentGoal.ad_spend_goal / daysInPeriod;

  const expectedSalesToDate =
    isCurrentPeriod ? dailySalesGoal * differenceInDays(new Date(), startDate)
    : isFuturePeriod ? 0
    : currentGoal.total_sales_goal;
  const expectedAdSpendToDate =
    isCurrentPeriod ? dailyAdSpendGoal * differenceInDays(new Date(), startDate)
    : isFuturePeriod ? 0
    : currentGoal.ad_spend_goal;

  return {
    expectedToDate: {
      totalSales: expectedSalesToDate,
      adSpend: expectedAdSpendToDate,
    },
    dailyGoal: {
      totalSales: dailySalesGoal,
      adSpend: dailyAdSpendGoal,
    },
    totalGoal: {
      totalSales: currentGoal.total_sales_goal,
      adSpend: currentGoal.ad_spend_goal,
      tacos: currentGoal.ad_spend_goal / currentGoal.total_sales_goal,
    },
    daysInPeriod,
  };
}
